// src/core/services/wodsService.ts
import { CategoryWod } from '../models/CategoryWod';
import { parseWod, Wod } from '../models/Wod';
import apiClient from './apiClient';
import type { AxiosError } from 'axios';
import {
  NoWodError,
  ApiError,
  NetworkError,
  UnauthorizedError,
  ForbiddenError,
  NotFoundError,
  InvalidResponseError,
} from './errors';

type ApiListResponse = {
  success?: boolean;
  status?: number;
  data?: unknown;
};

function isAxiosError(e: unknown): e is AxiosError {
  return Boolean(e && typeof e === 'object' && (e as any).isAxiosError);
}

function assertIsObject(
  val: unknown,
  message = 'Expected object',
): asserts val is Record<string, unknown> {
  if (typeof val !== 'object' || val === null) throw new InvalidResponseError(message);
}

function assertIsArray(val: unknown, message = 'Expected array'): asserts val is unknown[] {
  if (!Array.isArray(val)) throw new InvalidResponseError(message);
}

export const fetchWodDelDia = async (
  date: string,
  boxId: number,
  expand = 'blocks',
  typeId?: number,
): Promise<Wod[]> => {
  try {
    // date = '2025-09-18'; // moc para test
    // Si no se proporcionó typeId, intentar obtenerlo desde las categoryWods
    let typeIdToUse: number | undefined = typeId;
    if (typeIdToUse === undefined) {
      try {
        const cats = await fetchCategoryWods(boxId);
        if (Array.isArray(cats) && cats.length > 0) {
          const first = cats[0] as any;
          typeIdToUse = first?.id ?? first?.type_id ?? undefined;
        }
      } catch (e) {
        // si falla obtener categories, no rompemos — seguiremos sin filtro type_id
        typeIdToUse = undefined;
      }
    }

    const params: Record<string, any> = {
      'filter[date]': 'now',
      'filter[box_id]': boxId,
      expand,
      'filter[is_deleted]': 0,
      'filter[blocks.is_deleted]': 0,
    };
    if (typeIdToUse !== undefined) {
      // params["filter[type_id]"] = typeIdToUse;
    }

    const { data: resp } = await apiClient.get<ApiListResponse>('/wods', { params });

    // Validaciones básicas del payload
    assertIsObject(resp, 'Payload no es un objeto');
    const rawData = resp.data;
    assertIsArray(rawData, '`data` no es un array');

    // 🔍 DEBUG: Log de respuesta cruda de la API
    console.log('[API DEBUG] Respuesta cruda de /wods:', {
      totalWods: rawData.length,
      wods: (rawData as any[]).map((w: any) => ({
        id: w.id,
        title: w.title,
        blocksCount: Array.isArray(w.blocks) ? w.blocks.length : 0,
        blocks: Array.isArray(w.blocks)
          ? w.blocks.map((b: any) => ({
            id: b.id,
            title: b.title,
            work: b.work ? (b.work.length > 50 ? b.work.substring(0, 50) + '...' : b.work) : 'VACÍO',
            is_deleted: b.is_deleted,
          }))
          : [],
      })),
    });

    // 🔍 DEBUG: Log detallado expandido
    console.log('[API DEBUG] Detalle completo de WODs:', JSON.stringify((rawData as any[]).map((w: any) => ({
      id: w.id,
      title: w.title,
      blocksCount: Array.isArray(w.blocks) ? w.blocks.length : 0,
      blocks: Array.isArray(w.blocks)
        ? w.blocks.map((b: any) => ({
          id: b.id,
          title: b.title,
          work: b.work ? (b.work.length > 100 ? b.work.substring(0, 100) + '...' : b.work) : 'VACÍO',
          is_deleted: b.is_deleted,
        }))
        : [],
    })), null, 2));

    // Convertir con parseWod y filtrar borrados
    const wods: Wod[] = (rawData as any[])
      .map((item) => parseWod(item))
      .filter((w) => w.is_deleted !== 1);

    // 🔍 DEBUG: Log de WODs parseados
    console.log('[API DEBUG] WODs parseados después de filtrar:', {
      totalWods: wods.length,
      wods: wods.map((w) => ({
        id: w.id,
        title: w.title,
        blocksCount: w.blocks.length,
        blocks: w.blocks.map((b) => ({
          id: b.id,
          title: b.title,
          work: b.work ? (b.work.length > 50 ? b.work.substring(0, 50) + '...' : b.work) : 'VACÍO',
          is_deleted: b.is_deleted,
        })),
      })),
    });

    // 🔍 DEBUG: Log detallado expandido de WODs parseados
    console.log('[API DEBUG] Detalle completo de WODs parseados:', JSON.stringify(wods.map((w) => ({
      id: w.id,
      title: w.title,
      blocksCount: w.blocks.length,
      blocks: w.blocks.map((b) => ({
        id: b.id,
        title: b.title,
        work: b.work ? (b.work.length > 100 ? b.work.substring(0, 100) + '...' : b.work) : 'VACÍO',
        is_deleted: b.is_deleted,
      })),
    })), null, 2));

    if (wods.length === 0) {
      throw new NoWodError(date);
    }

    return wods;
  } catch (err: unknown) {
    if (isAxiosError(err)) {
      if (!err.response) {
        throw new NetworkError(err.message);
      }

      const status = err.response.status;
      switch (status) {
        case 401:
          throw new UnauthorizedError();
        case 403:
          throw new ForbiddenError();
        case 404:
          throw new NotFoundError();
        default:
          throw new ApiError(status, err.message);
      }
    }

    throw err;
  }
};

export async function fetchCategoryWods(boxId: number): Promise<CategoryWod[]> {
  try {
    const response = await apiClient.get<{
      success?: boolean;
      status?: number;
      data?: CategoryWod[];
    }>(`/wods/types/${boxId}`);

    if (!response || typeof response !== 'object')
      throw new InvalidResponseError('Invalid response');
    if (!Array.isArray(response.data?.data))
      throw new InvalidResponseError('`data` is not an array');

    return response.data.data;
  } catch (err: unknown) {
    if (isAxiosError(err)) {
      if (!err.response) throw new NetworkError(err.message);
      switch (err.response.status) {
        case 401:
          throw new UnauthorizedError();
        case 403:
          throw new ForbiddenError();
        case 404:
          throw new NotFoundError();
        default:
          throw new ApiError(err.response.status, err.message);
      }
    }
    throw err;
  }
}
